import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  styled,
  useMediaQuery,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Layout from "../../components/Layout";
import { useAppDispatch, useAppSelector } from "../../redux";
import { AppDispatch } from "../../redux/store";
import { getBreefInviteUserDetails, resendBreefInvite } from "../../service/BreefInviteService";
import { BreefInviteUserDetail } from "../../redux/slices/BreefInviteSlice";
import { showErrorToast, showSuccessToast } from "../../utils/Toast";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  fontWeight: "bold",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableWrapper = styled(TableContainer)({
  margin: "auto",
  marginTop: "1%",
  width: "90%",
}) as typeof TableContainer;

const statusColor = (status: BreefInviteUserDetail["sendStatus"]) => {
  if (status === "SENT") return "success";
  if (status === "FAILED") return "error";
  return "warning";
};

export default function BreefTrackingDetail() {
  const { breefId } = useParams();
  const isMobile = useMediaQuery("(max-width:600px)");
  const dispatch: AppDispatch = useAppDispatch();
  const data = useAppSelector((state) => state.breefInvites);
  const navigate = useNavigate();
  const [resendingId, setResendingId] = useState<string | null>(null);

  const fetchDetails = async () => {
    if (breefId) {
      await getBreefInviteUserDetails(dispatch, isMobile, navigate, breefId);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [breefId]);

  const handleResend = async (inviteId: string) => {
    setResendingId(inviteId);
    try {
      const result = await resendBreefInvite(inviteId, isMobile, navigate);
      if (result?.success) {
        showSuccessToast("Invite resent successfully", isMobile);
      } else {
        showErrorToast(result?.error || "Resend failed", isMobile);
      }
      await fetchDetails();
    } finally {
      setResendingId(null);
    }
  };

  return (
    <Layout>
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
        <h1>Breef Invite Delivery Status</h1>
      </Box>
      <TableWrapper component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                User
              </StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                Destination
              </StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                Medium
              </StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                Status
              </StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                Attempts
              </StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                Last Attempt
              </StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                Error
              </StyledTableCell>
              <StyledTableCell sx={{ backgroundColor: "#ED670A" }} align="center">
                Actions
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.userDetailsData.results.map((invite) => (
              <StyledTableRow key={invite.inviteId}>
                <TableCell align="center">{invite.userName}</TableCell>
                <TableCell align="center">{invite.destination || "-"}</TableCell>
                <TableCell align="center">
                  <Chip label={invite.inviteType.toUpperCase()} size="small" />
                </TableCell>
                <TableCell align="center">
                  {invite.sendStatus ? (
                    <Chip label={invite.sendStatus} color={statusColor(invite.sendStatus)} size="small" />
                  ) : (
                    <Tooltip title="This invite was sent before delivery tracking existed - no send outcome was recorded for it">
                      <Chip label="Not tracked" size="small" variant="outlined" />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell align="center">{invite.attempts !== undefined ? `${invite.attempts}/3` : "-"}</TableCell>
                <TableCell align="center">
                  {invite.lastAttemptAt ? new Date(invite.lastAttemptAt).toLocaleString() : "-"}
                </TableCell>
                <TableCell align="center">
                  {invite.sendError ? (
                    <Tooltip title={invite.sendError}>
                      <Box
                        sx={{
                          maxWidth: 160,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {invite.sendError}
                      </Box>
                    </Tooltip>
                  ) : (
                    "-"
                  )}
                </TableCell>
                <TableCell align="center">
                  {invite.sendStatus === "FAILED" && (invite.attempts ?? 0) < 3 && (
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={resendingId === invite.inviteId}
                      onClick={() => handleResend(invite.inviteId)}
                    >
                      {resendingId === invite.inviteId ? "Resending..." : "Resend"}
                    </Button>
                  )}
                </TableCell>
              </StyledTableRow>
            ))}
            {data.userDetailsData.results.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No invites found for this breef
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableWrapper>
    </Layout>
  );
}
